import type { Action, WireState } from '@nt2/engine';

/**
 * How the client talks to the authority.
 *
 * Both modes present the identical surface, so the whole UI is written once
 * and solo play is not a special case with its own bugs: it is simply a room
 * whose server happens to live in the same tab.
 */
export interface Transport {
  send(action: Action): void;
  close(): void;
  readonly kind: 'local' | 'remote';
}

export interface TransportHandlers {
  onState(state: WireState): void;
  onEvents(events: unknown[]): void;
  onError(message: string): void;
  onStatus(status: ConnectionStatus): void;
}

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed' | 'rejected';

export interface ServerMessage {
  type: 'state' | 'events' | 'error' | 'hello';
  state?: WireState;
  events?: unknown[];
  message?: string;
}

/**
 * WebSocket transport with reconnect.
 *
 * Reconnection matters more here than in most apps: a player on a phone will
 * lock the screen mid-match, and coming back to a dead room with a grid they
 * spent ten minutes on would be the worst possible failure. The seat is held
 * server-side and the socket re-attaches to it with the same identity.
 */
/** How often a live connection proves it is still alive. */
const HEARTBEAT_MS = 20_000;
/** How long to wait for the answer before calling the connection dead. */
const PONG_WAIT_MS = 5_000;
/** The quicker check made the moment the app comes back to the foreground. */
const RETURN_WAIT_MS = 2_000;
/** Away longer than this, and the connection is replaced without asking. */
const LONG_AWAY_MS = 10_000;

export class WsTransport implements Transport {
  readonly kind = 'remote';
  #ws: WebSocket | null = null;
  #closed = false;
  #attempt = 0;
  #queue: Action[] = [];
  #timer: number | null = null;
  #beat: number | null = null;
  #deadline: number | null = null;

  /*
   * Coming back to the app.
   *
   * A phone freezes a backgrounded page and its socket dies without either
   * end being told. The page then shows a table that has stopped updating, and
   * nothing corrects it until TCP gives up — often half a minute. That is what
   * a host saw after switching away to share the invite: the guest who had
   * just joined simply did not appear. So on returning, the connection is
   * checked at once and replaced if it does not answer within two seconds;
   * and a reconnect that was waiting out a back-off happens immediately.
   */
  #onReturn = (): void => {
    if (this.#closed) return;
    if (document.visibilityState !== 'visible') { this.#hiddenAt = Date.now(); return; }
    const away = this.#hiddenAt === null ? 0 : Date.now() - this.#hiddenAt;
    this.#hiddenAt = null;
    // Away long enough to share a link or answer a message, and the socket is
    // very probably dead: dialling again at once beats waiting two seconds to
    // find out. A glance away gets the cheaper check first.
    if (!this.#ws || away > LONG_AWAY_MS) { this.#reconnectNow(); return; }
    this.#probe(RETURN_WAIT_MS);
  };
  #hiddenAt: number | null = null;

  constructor(private url: string, private h: TransportHandlers) {
    document.addEventListener('visibilitychange', this.#onReturn);
    window.addEventListener('online', this.#onReturn);
    window.addEventListener('pageshow', this.#onReturn);
    this.#open();
  }

  /** Ask the server for a "pong"; no answer in time means the socket is dead. */
  #probe(wait: number): void {
    const ws = this.#ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) { this.#reconnectNow(); return; }
    if (this.#deadline !== null) return;             // one already in flight
    try { ws.send('ping'); } catch { this.#reconnectNow(); return; }
    this.#deadline = window.setTimeout(() => {
      this.#deadline = null;
      this.#reconnectNow();
    }, wait);
  }

  /** Abandon the current socket without waiting for it to close, and dial again. */
  #reconnectNow(): void {
    if (this.#closed) return;
    this.#stopBeat();
    const old = this.#ws;
    this.#ws = null;
    if (old) {
      old.onopen = old.onmessage = old.onclose = old.onerror = null;
      // Not 1000 "bye": to the server this is a lost signal, not a departure.
      try { old.close(4000, 'reconnecting'); } catch { /* already gone */ }
    }
    if (this.#timer !== null) { clearTimeout(this.#timer); this.#timer = null; }
    this.#attempt = Math.max(this.#attempt, 1);
    this.#open();
  }

  #stopBeat(): void {
    if (this.#beat !== null) { clearInterval(this.#beat); this.#beat = null; }
    if (this.#deadline !== null) { clearTimeout(this.#deadline); this.#deadline = null; }
  }

  #open(): void {
    if (this.#closed) return;
    this.h.onStatus(this.#attempt === 0 ? 'connecting' : 'reconnecting');
    let ws: WebSocket;
    try { ws = new WebSocket(this.url); }
    catch { this.#retry(); return; }
    this.#ws = ws;

    ws.onopen = () => {
      this.#attempt = 0;
      this.h.onStatus('open');
      const pending = this.#queue;
      this.#queue = [];
      for (const a of pending) this.send(a);
      this.#stopBeat();
      this.#beat = window.setInterval(() => {
        // A hidden page's timers are throttled anyway; the return check above
        // covers that case properly.
        if (document.visibilityState === 'visible') this.#probe(PONG_WAIT_MS);
      }, HEARTBEAT_MS);
    };
    ws.onmessage = (ev) => {
      if (ev.data === 'pong') {
        if (this.#deadline !== null) { clearTimeout(this.#deadline); this.#deadline = null; }
        return;
      }
      let msg: ServerMessage;
      try { msg = JSON.parse(String(ev.data)) as ServerMessage; } catch { return; }
      if (msg.type === 'state' && msg.state) this.h.onState(msg.state);
      else if (msg.type === 'events' && msg.events) this.h.onEvents(msg.events);
      else if (msg.type === 'error' && msg.message) this.h.onError(msg.message);
    };
    ws.onclose = (ev) => {
      this.#ws = null;
      this.#stopBeat();
      if (this.#closed) { this.h.onStatus('closed'); return; }
      // 4403 is the room refusing us: a bad invite, a locked room, or a full
      // table. Retrying would just hammer it, so stop and say so.
      if (ev.code === 4403) { this.#closed = true; this.h.onStatus('rejected'); return; }
      this.#retry();
    };
    ws.onerror = () => { /* onclose always follows; handled there */ };
  }

  #retry(): void {
    if (this.#closed || this.#timer !== null) return;
    this.#attempt++;
    const wait = Math.min(15_000, 500 * 2 ** Math.min(this.#attempt, 5))
      + Math.random() * 400;               // jitter, so 20 phones do not sync up
    this.h.onStatus('reconnecting');
    this.#timer = window.setTimeout(() => { this.#timer = null; this.#open(); }, wait);
  }

  send(action: Action): void {
    const ws = this.#ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'action', action }));
    } else if (this.#queue.length < 200) {
      this.#queue.push(action);
    }
  }

  /** Leave for good. "bye" is what tells the server this is a departure. */
  close(): void {
    this.#closed = true;
    this.#stopBeat();
    document.removeEventListener('visibilitychange', this.#onReturn);
    window.removeEventListener('online', this.#onReturn);
    window.removeEventListener('pageshow', this.#onReturn);
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#ws?.close(1000, 'bye');
    this.#ws = null;
  }
}
